import * as React from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";

export type ModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  className?: string;
};

export function Modal({ open, onOpenChange, children }: ModalProps) {
  React.useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onOpenChange(false);
    }
    if (open) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={() => onOpenChange(false)}
        aria-hidden
      />
      <div className="absolute inset-0 flex items-center justify-center px-4">
        {children}
      </div>
    </div>,
    document.body
  );
}

export function ModalContent(
  { className = "", ...props }: React.HTMLAttributes<HTMLDivElement>
) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      className={cn(
        "w-full max-w-lg rounded-xl border border-border bg-card text-card-foreground shadow-lg focus:outline-none",
        className
      )}
      {...props}
    />
  );
}

export function ModalHeader(
  { className = "", ...props }: React.HTMLAttributes<HTMLDivElement>
) {
  return <div className={cn("p-6 pb-2", className)} {...props} />;
}

export function ModalTitle(
  { className = "", ...props }: React.HTMLAttributes<HTMLHeadingElement>
) {
  return (
    <h3 className={cn("text-lg font-semibold leading-none tracking-tight", className)} {...props} />
  );
}

export function ModalDescription(
  { className = "", ...props }: React.HTMLAttributes<HTMLParagraphElement>
) {
  return <p className={cn("text-sm text-muted-foreground", className)} {...props} />;
}

export function ModalFooter(
  { className = "", ...props }: React.HTMLAttributes<HTMLDivElement>
) {
  return <div className={cn("p-6 pt-4 flex justify-end gap-3", className)} {...props} />;
}
